import { Link } from "react-router-dom";
import { Instagram, MapPin, Mail, Phone } from "lucide-react";
import logoWhite from "@/assets/ping-logo-white.png";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function Footer() {
  const settings = useSiteSettings();
  const phoneDisplay = settings?.phone_number || "+91-9899039199";
  const waNumber = (settings?.whatsapp_number || "919899039199").replace(/[^\d]/g, "");
  const waMsg = encodeURIComponent(settings?.whatsapp_message || "");
  const waHref = `https://wa.me/${waNumber}${waMsg ? `?text=${waMsg}` : ""}`;
  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="site-footer">
      <div className="border-b border-[#27272A] overflow-hidden py-4">
        <div className="marquee whitespace-nowrap flex">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center shrink-0">
              {["Custom Fit", "Custom Built", "Custom Engineered", "Engineered In Phoenix", "Played Worldwide", "PING India"].map((t, i) => (
                <span key={i} className="mx-8 text-2xl font-black uppercase tracking-tight font-head text-white/90">
                  {t} <span className="text-[#E31837]">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="container-ping py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-baseline gap-2">
            <img src={logoWhite} alt="PING" className="h-8 w-auto" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/50">India</span>
          </div>
          <p className="mt-4 text-sm text-white/60 leading-relaxed max-w-xs">
            <span className="text-white/90 font-semibold">J&amp;B Golf</span> — Sole Distributor for PING in India. From clubs and custom fitting to bags, apparel and accessories, we bring the full PING line-up to golfers across India with genuine products and expert service.
          </p>
          <div className="flex gap-4 mt-6">
            <a href="https://www.instagram.com/pinggolfindia" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram-link" className="hover:text-[#E31837] btn-transition"><Instagram className="h-5 w-5" /></a>
          </div>
        </div>

        <div>
          <h4 className="label-caps text-white/50 mb-5">Equipment</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/equipment/drivers" className="hover:text-[#E31837] btn-transition">Drivers</Link></li>
            <li><Link to="/equipment/driving-irons" className="hover:text-[#E31837] btn-transition">Driving Irons</Link></li>
            <li><Link to="/equipment/irons" className="hover:text-[#E31837] btn-transition">Irons</Link></li>
            <li><Link to="/equipment/wedges" className="hover:text-[#E31837] btn-transition">Wedges</Link></li>
            <li><Link to="/equipment/putters" className="hover:text-[#E31837] btn-transition">Putters</Link></li>
            <li><Link to="/equipment/ladies" className="hover:text-[#E31837] btn-transition">Ladies</Link></li>
            <li><Link to="/equipment/juniors" className="hover:text-[#E31837] btn-transition">Junior</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="label-caps text-white/50 mb-5">Pro Shop</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/shop/bags" className="hover:text-[#E31837] btn-transition">Bags</Link></li>
            <li><Link to="/shop/apparel" className="hover:text-[#E31837] btn-transition">Apparel</Link></li>
            <li><Link to="/shop/gloves" className="hover:text-[#E31837] btn-transition">Gloves</Link></li>
            <li><Link to="/shop/accessories" className="hover:text-[#E31837] btn-transition">Accessories</Link></li>
          </ul>
        </div>

        <div className="col-span-2 md:col-span-1">
          <h4 className="label-caps text-white/50 mb-5">Get in Touch</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Khasra No. 1151/3, Ward No. 7, 4th Floor, Plot No. 1064B, Mehrauli, New Delhi 110030, India</span>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <span>{phoneDisplay}</span>
              <a href={waHref} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp" data-testid="footer-whatsapp-link" className="ml-1 inline-flex items-center text-[#25D366] hover:opacity-80 btn-transition">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> contact@pinggolf.in</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#27272A]">
        <div className="container-ping py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/40">
          <p>© {new Date().getFullYear()} J&amp;B Golf · Sole Distributor for PING in India. All rights reserved.</p>
          <p>PING and the PING logo are trademarks of Karsten Manufacturing Corporation.</p>
        </div>
      </div>
    </footer>
  );
}
